// src/services/userActivity.ts
// Privacy-safe, production activity pulse for aggregate admin metrics.
// This is intentionally separate from Crew Presence: no GPS, no crew ACLs,
// no relationship data, and no per-user activity UI.

import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';

export const USER_ACTIVITY_PULSE_MS = 5 * 60 * 1000;
export const USER_ACTIVITY_ONLINE_WINDOW_MS = 15 * 60 * 1000;

const MIN_WRITE_GAP_MS = 4 * 60 * 1000;

let lastWriteAt = 0;
let writeInFlight: Promise<void> | null = null;

function currentPlatform(): 'web' | 'ios' | 'android' {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios' || platform === 'android') return platform;
  return 'web';
}

async function pulseUserActivity(uid: string, force = false): Promise<void> {
  const cleanUid = String(uid || '').trim();
  if (!cleanUid) return;

  // Firestore security rules also enforce ownership. This client-side check
  // prevents useless denied writes while native -> JS auth is still bridging.
  if (auth.currentUser?.uid !== cleanUid) return;

  const now = Date.now();
  if (!force && now - lastWriteAt < MIN_WRITE_GAP_MS) return;
  if (writeInFlight) return writeInFlight;

  const task = setDoc(
    doc(db, 'userActivity', cleanUid),
    {
      uid: cleanUid,
      schemaVersion: 1,
      platform: currentPlatform(),
      lastActiveAt: serverTimestamp(),
    },
    { merge: true },
  )
    .then(() => {
      lastWriteAt = Date.now();
    })
    .catch((error) => {
      // Activity telemetry must never interfere with normal app use.
      console.warn('[userActivity] pulse skipped', error);
    })
    .finally(() => {
      writeInFlight = null;
    });

  writeInFlight = task;
  return task;
}

export function startUserActivityPulse(uid: string): () => void {
  const cleanUid = String(uid || '').trim();
  if (!cleanUid) return () => {};

  let disposed = false;
  let nativeHandle: { remove: () => Promise<void> } | null = null;

  const pulseIfForegrounded = (force = false) => {
    if (disposed) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    void pulseUserActivity(cleanUid, force);
  };

  // Session start / authenticated app-ready pulse.
  pulseIfForegrounded(true);

  const interval = window.setInterval(
    () => pulseIfForegrounded(false),
    USER_ACTIVITY_PULSE_MS,
  );

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') pulseIfForegrounded(false);
  };
  const onFocus = () => pulseIfForegrounded(false);
  const onPageShow = () => pulseIfForegrounded(false);

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('focus', onFocus);
  window.addEventListener('pageshow', onPageShow);

  if (Capacitor.isNativePlatform()) {
    void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) pulseIfForegrounded(false);
    })
      .then((handle) => {
        if (disposed) {
          void handle.remove();
          return;
        }
        nativeHandle = handle;
      })
      .catch((error) =>
        console.warn('[userActivity] native app-state listener skipped', error),
      );
  }

  return () => {
    disposed = true;
    window.clearInterval(interval);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('focus', onFocus);
    window.removeEventListener('pageshow', onPageShow);
    if (nativeHandle) void nativeHandle.remove();
  };
}
